import React, { useState } from "react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { useAuth } from "../contexts/AuthContext";
import { useI18n } from "../contexts/I18nContext";
import { api } from "../lib/api";
import { Check, X } from "lucide-react";

const PLAN_META = {
  starter:    { monthlyId: "starter_monthly",    yearlyId: "starter_yearly",    priceM: 7,  priceY: 70 },
  pro:        { monthlyId: "pro_monthly",        yearlyId: "pro_yearly",        priceM: 19, priceY: 190 },
  enterprise: { monthlyId: "enterprise_monthly", yearlyId: "enterprise_yearly", priceM: 49, priceY: 490 },
};

function ContactDialog({ open, onClose }) {
  const { t, lang } = useI18n();
  const [form, setForm] = useState({ name: "", email: "", firm: "", message: "" });
  const [sending, setSending] = useState(false);

  if (!open) return null;
  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/contact/sales", form);
      toast.success(t("common.success"));
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("common.error"));
    } finally { setSending(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center p-4" data-testid="contact-dialog">
      <div className="card-premium p-8 w-full max-w-md bg-white relative">
        <button onClick={onClose} className="absolute top-4 right-4 text-slate-400 hover:text-slate-700" data-testid="contact-close">
          <X className="h-5 w-5" />
        </button>
        <div className="kicker">Enterprise</div>
        <h2 className="mt-2 font-display font-bold text-2xl text-slate-900">
          {lang === "el" ? "Ας μιλήσουμε" : "Let's talk"}
        </h2>
        <p className="mt-2 text-sm text-slate-600">
          {lang === "el"
            ? "Συμπληρώστε τα στοιχεία σας και θα επικοινωνήσουμε εντός 1 εργάσιμης ημέρας."
            : "Leave your details and we'll get back within 1 business day."}
        </p>
        <form onSubmit={submit} className="mt-6 space-y-4">
          {[
            { k: "name", label: lang === "el" ? "Ονοματεπώνυμο" : "Full name", required: true },
            { k: "email", label: t("auth.email"), type: "email", required: true },
            { k: "firm", label: t("auth.firm") },
          ].map((f) => (
            <input key={f.k} type={f.type || "text"} placeholder={f.label} required={f.required}
              value={form[f.k]} onChange={(e) => setForm({ ...form, [f.k]: e.target.value })}
              className="w-full rounded-lg border border-slate-300 bg-white px-4 py-2.5 focus:border-[#0A192F] focus:ring-1 focus:ring-[#0A192F] outline-none transition text-sm"
              data-testid={`contact-${f.k}`} />
          ))}
          <textarea rows={4} placeholder={lang === "el" ? "Μήνυμα" : "Message"}
            value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })}
            className="w-full rounded-lg border border-slate-300 bg-white px-4 py-2.5 focus:border-[#0A192F] focus:ring-1 focus:ring-[#0A192F] outline-none transition text-sm"
            data-testid="contact-message" />
          <button disabled={sending} className="btn-primary w-full rounded-full px-6 py-3 font-semibold disabled:opacity-60" data-testid="contact-submit">
            {sending ? t("common.loading") : (lang === "el" ? "Αποστολή" : "Send")}
          </button>
        </form>
      </div>
    </div>
  );
}

export default function Pricing() {
  const { t, lang } = useI18n();
  const { user } = useAuth();
  const nav = useNavigate();
  const [yearly, setYearly] = useState(false);
  const [loading, setLoading] = useState("");
  const [contactOpen, setContactOpen] = useState(false);

  const checkout = async (planKey) => {
    if (!user) { nav("/signup"); return; }
    const meta = PLAN_META[planKey];
    const plan_id = yearly ? meta.yearlyId : meta.monthlyId;
    setLoading(planKey);
    try {
      const res = await api.post("/billing/checkout", { plan_id, origin_url: window.location.origin });
      window.location.href = res.data.url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("common.error"));
    } finally { setLoading(""); }
  };

  const FreeCard = () => (
    <div className="rounded-2xl p-8 border border-slate-200 bg-white text-slate-900 shadow-[0_8px_30px_rgba(0,0,0,0.04)]" data-testid="pricing-free">
      <div className="text-xs font-semibold tracking-[0.2em] uppercase text-[#8B1E3F]">{t("landing.pricingFreeName")}</div>
      <div className="mt-4 flex items-baseline gap-1">
        <span className="font-display font-bold text-5xl tracking-tight">€0</span>
        <span className="text-sm text-slate-500">{t("landing.pricingPerMonth")}</span>
      </div>
      <ul className="mt-6 space-y-2 text-sm text-slate-600">
        {t("landing.planFeaturesFree").map((f, i) => (
          <li key={i} className="flex items-start gap-2">
            <Check className="h-4 w-4 mt-0.5 flex-none text-[#8B1E3F]" /><span>{f}</span>
          </li>
        ))}
      </ul>
      <button
        onClick={() => nav(user ? "/dashboard" : "/signup")}
        className="mt-7 block w-full text-center rounded-full px-5 py-2.5 font-medium transition border border-slate-300 text-slate-800 hover:bg-slate-50"
        data-testid="pricing-free-cta"
      >
        {t("landing.pricingFreeCta")}
      </button>
    </div>
  );

  const PaidCard = ({ planKey, tier, features, ctaLabel, highlighted }) => {
    const meta = PLAN_META[planKey];
    const price = yearly ? meta.priceY : meta.priceM;
    const period = yearly ? t("landing.pricingPerYear") : t("landing.pricingPerMonth");
    const isEnterprise = planKey === "enterprise";
    return (
      <div className={`relative rounded-2xl p-8 border transition ${
        highlighted ? "bg-[#0A192F] text-white border-[#0A192F] shadow-[0_28px_60px_rgba(11,59,111,0.22)]"
                    : "bg-white text-slate-900 border-slate-200 shadow-[0_8px_30px_rgba(0,0,0,0.04)]"
      }`} data-testid={`pricing-${planKey}`}>
        {highlighted && (
          <div className="absolute -top-3 left-6 bg-[#8B1E3F] text-white text-[10px] font-bold tracking-[0.18em] uppercase px-3 py-1 rounded-full">
            {t("billing.mostPopular")}
          </div>
        )}
        <div className={`text-xs font-semibold tracking-[0.2em] uppercase ${highlighted ? "text-[#7DD3DC]" : "text-[#8B1E3F]"}`}>{tier}</div>
        <div className="mt-4 flex items-baseline gap-1">
          <span className="font-display font-bold text-5xl tracking-tight">€{price}</span>
          <span className={`text-sm ${highlighted ? "text-blue-100" : "text-slate-500"}`}>{period}</span>
        </div>
        <ul className={`mt-6 space-y-2 text-sm ${highlighted ? "text-blue-50" : "text-slate-600"}`}>
          {features.map((f, i) => (
            <li key={i} className="flex items-start gap-2">
              <Check className={`h-4 w-4 mt-0.5 flex-none ${highlighted ? "text-[#7DD3DC]" : "text-[#8B1E3F]"}`} />
              <span>{f}</span>
            </li>
          ))}
        </ul>
        {isEnterprise ? (
          <button onClick={() => setContactOpen(true)}
            className="mt-7 block w-full text-center rounded-full px-5 py-2.5 font-medium transition border border-slate-300 text-slate-800 hover:bg-slate-50"
            data-testid={`pricing-${planKey}-cta`}>
            {ctaLabel}
          </button>
        ) : (
          <button onClick={() => checkout(planKey)} disabled={loading === planKey}
            className={`mt-7 block w-full text-center rounded-full px-5 py-2.5 font-medium transition ${
              highlighted ? "bg-white text-[#0A192F] hover:bg-slate-100" : "btn-primary"
            } disabled:opacity-60`}
            data-testid={`pricing-${planKey}-cta`}>
            {loading === planKey ? t("common.loading") : ctaLabel}
          </button>
        )}
      </div>
    );
  };

  return (
    <>
      <Navbar />
      <main className="bg-[#FAFAFA]">
        <section className="max-w-7xl mx-auto px-6 md:px-10 pt-16 pb-24">
          <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-6">
            <div>
              <div className="kicker">{t("landing.pricingKicker")}</div>
              <h1 className="mt-4 font-display font-extrabold text-4xl lg:text-5xl tracking-tight text-slate-900">
                {t("billing.title")}
              </h1>
              {user && <p className="mt-3 text-slate-500 text-sm">{t("billing.currentPlan")}: <span className="font-medium text-slate-900">{user.plan || "Free"}</span></p>}
            </div>
            <div className="inline-flex items-center rounded-full border border-slate-200 bg-white p-1" data-testid="pricing-page-toggle">
              <button onClick={() => setYearly(false)} data-testid="pricing-page-monthly"
                      className={`px-4 py-2 text-sm rounded-full transition ${!yearly ? "bg-[#0A192F] text-white" : "text-slate-600"}`}>
                {t("landing.pricingMonthly")}
              </button>
              <button onClick={() => setYearly(true)} data-testid="pricing-page-yearly"
                      className={`px-4 py-2 text-sm rounded-full transition ${yearly ? "bg-[#0A192F] text-white" : "text-slate-600"}`}>
                {t("landing.pricingYearly")}
              </button>
            </div>
          </div>
          <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <FreeCard />
            <PaidCard planKey="starter"    tier={t("landing.pricingStarter")}    features={t("landing.planFeaturesStarter")}    ctaLabel={t("landing.pricingStarterCta")} />
            <PaidCard planKey="pro"        tier={t("landing.pricingPro")}        features={t("landing.planFeaturesPro")}        ctaLabel={t("landing.pricingProCta")} highlighted />
            <PaidCard planKey="enterprise" tier={t("landing.pricingEnterprise")} features={t("landing.planFeaturesEnterprise")} ctaLabel={t("landing.pricingEnterpriseCta")} />
          </div>
        </section>
      </main>
      <Footer />
      <ContactDialog open={contactOpen} onClose={() => setContactOpen(false)} />
    </>
  );
}
